import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { Plus, ShieldCheck, Users, FileText, ClipboardCheck, Clock, Wallet } from 'lucide-react';
import { getBackendBaseUrl } from '../lib/backendUrl';
import { adminApi } from '../api/examApi';

function StatCard({ icon: Icon, label, value, to, color = 'primary' }) {
  const colorMap = {
    primary: 'bg-primary/10 text-primary',
    amber: 'bg-amber-500/10 text-amber-500',
    emerald: 'bg-emerald-500/10 text-emerald-500',
    rose: 'bg-rose-500/10 text-rose-500',
    violet: 'bg-violet-500/10 text-violet-500',
  };
  const card = (
    <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:border-primary hover:shadow-lg transition-all">
      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${colorMap[color]} mb-4`}>
        <Icon size={22} />
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white">{value ?? '—'}</p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('inspire_token');
    fetch(`${getBackendBaseUrl()}/api/v1/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setStats(data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout title="Admin Panel">
      <div className="animate-fade-in space-y-8">

        {/* Stats row */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            icon={Clock}
            label="Pending Approvals"
            value={loading ? '…' : (stats?.pending_students ?? 0)}
            to="/admin/students?status=pending"
            color="amber"
          />
          <StatCard
            icon={Users}
            label="Total Students"
            value={loading ? '…' : (stats?.total_students ?? 0)}
            to="/admin/students"
            color="primary"
          />
          <StatCard
            icon={FileText}
            label="Active Exams"
            value={loading ? '…' : (stats?.active_exams ?? 0)}
            to="/admin/exams"
            color="emerald"
          />
          <StatCard
            icon={ClipboardCheck}
            label="Pending Evaluations"
            value={loading ? '…' : (stats?.pending_evaluations ?? 0)}
            color="rose"
          />
          <StatCard
            icon={Wallet}
            label="Fees Due / Overdue"
            value={loading ? '…' : (stats?.fees_attention_count ?? 0)}
            to="/admin/students?fees=attention"
            color="violet"
          />
        </div>

        {/* Action cards */}
        <div className="grid gap-6 sm:grid-cols-2">
          <Link
            to="/admin/faculty/create"
            className="group block rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:border-primary hover:shadow-lg transition-all"
            data-testid="admin-create-faculty-card"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary mb-5">
              <Plus size={24} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Create Faculty Account</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Add a new faculty member and assign their login credentials.
            </p>
          </Link>

          <Link
            to="/admin/students?status=pending"
            className="group block rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm hover:border-amber-500 hover:shadow-lg transition-all"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 mb-5">
              <Users size={24} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Student Approvals</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Review and approve or reject pending student registrations.
            </p>
            {stats?.pending_students > 0 && (
              <span className="mt-4 inline-block rounded-full bg-amber-100 dark:bg-amber-900/30 px-3 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                {stats.pending_students} pending
              </span>
            )}
          </Link>

          <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500">
                <ShieldCheck size={24} />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Admin Access</p>
                <p className="text-xl font-semibold text-slate-900 dark:text-white">Secure user management</p>
              </div>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Manage faculty and student accounts centrally. Approve student registrations before they can take exams.
            </p>
          </div>
        </div>

        <ChangePasswordCard
          onChange={(currentPassword, newPassword) =>
            adminApi.changePassword({ current_password: currentPassword, new_password: newPassword })
          }
        />
      </div>
    </DashboardLayout>
  );
}
